import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../../store/store';
import { INDIVIDUALS_SEARCH_CATEGORY_FIELDS } from '../../../../utils/constants/fields';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import Radio from '../../../../components/inputs/Radio';
import MultiSelect from '../../../../components/inputs/MultiSelect';
import ProfilerDemoMessage from './shared/ProfilerDemoMessage';
import IndividualsNameSearch from './individuals-name/IndividualsNameSearch';

/**
 * ProfilerIndividualsSearch - Orchestrator component for Individuals tab
 *
 * Manages the Individuals search tab content.
 * Currently only supports name search, but designed to support
 * additional search types in the future (e.g., Characteristics).
 *
 * TD-1709: Now uses URL-based navigation for individual details (standalone pages).
 * Search query can be restored from URL parameter when navigating back.
 *
 * @param {React.ReactNode} tabs - Tab navigation component
 * @param {React.ReactNode} searchTypeInput - Search type input for mobile (Profile dropdown)
 * @param {string} initialSearchQuery - Initial search query from URL parameter (for restoration)
 */
const ProfilerIndividualsSearch = observer(({ tabs, searchTypeInput, initialSearchQuery }) => {
  const { utilsStore } = useStore();

  const state = useLocalObservable(() => ({
    categoryFields: {
      [INDIVIDUALS_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]: [
        UI_OPTIONS[UI_OPTION_KEYS.INDIVIDUALS_SEARCH_CATEGORY]['Name']
      ]
    },
    setCategoryFieldValue: (field = {}, value) => {
      state.categoryFields[field.NAME] = value;
    },
    get categories() {
      return Object.entries(UI_OPTIONS[UI_OPTION_KEYS.INDIVIDUALS_SEARCH_CATEGORY]).map(
        ([name, value]) => ({ name, value })
      );
    },
    get layout() {
      return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
    }
  }));

  // Desktop layout
  if (state.layout === 'desktop') {
    return (
      <div className="formform desktop individuals-search-form">
        <div className="fields-area">
          {tabs}
          <div className="row category-group">
            <Radio
              field={INDIVIDUALS_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY}
              value={state.categoryFields[INDIVIDUALS_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]}
              setFieldValue={state.setCategoryFieldValue}
              values={state.categories}
            />
          </div>
          <div className="flip-horizontal-fields-area" style={{ minHeight: '320px' }}>
            <ProfilerDemoMessage />
            <IndividualsNameSearch
              inputId="ps-input-individuals-searchKey"
              initialSearchQuery={initialSearchQuery}
            />
          </div>
        </div>
      </div>
    );
  }

  // Mobile layout
  return (
    <div className="formform mobile individuals-search-form">
      <ProfilerDemoMessage />
      <div className="mobile-search-form-title">Select search type of preference</div>
      {searchTypeInput}
      <MultiSelect
        field={INDIVIDUALS_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE}
        value={state.categoryFields[INDIVIDUALS_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]}
        setFieldValue={state.setCategoryFieldValue}
        values={state.categories.filter(({ disabled }) => !disabled)}
      />
      <IndividualsNameSearch
        inputId="ps-input-individuals-searchKey-mobile"
        initialSearchQuery={initialSearchQuery}
      />
    </div>
  );
});

export default ProfilerIndividualsSearch;
